import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Star as StarIcon,
  MonetizationOn as MonetizationOnIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/api';
import TfiatDistributionSummary from './TfiatDistributionSummary';

interface FinancialSystemData {
  starDistribution: {
    total_stars: {
      guia: number;
      calificada: number;
      reincorporada: number;
    };
    total_users: number;
    users_with_guide_stars: number;
    total_stars_count: number;
  };
  tfiatDistribution: {
    total_balance_deductivo: number;
    total_unrealized_profit: number;
    total_realized_profit: number;
    total_company_pending: number;
    total_company_debt: number;
    total_company_deductive: number;
  };
  userStats: {
    total_users: number;
    active_users: number;
    users_with_company_host: number;
    company_exists: boolean;
  };
  systemHealth: {
    is_valid: boolean;
    last_distribution: string;
    distribution_transactions: number;
  };
}

const FinancialSystemPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<FinancialSystemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  useEffect(() => {
    fetchFinancialSystemData();
  }, []);

  const fetchFinancialSystemData = async () => {
    try {
      setLoading(true);
      const [starStatus, compensationData] = await Promise.all([
        adminService.getTfiatDistributionStatus(),
        adminService.getCompanyMetrics()
      ]);

      const financialData: FinancialSystemData = {
        starDistribution: starStatus.star_distribution || {
          total_stars: { guia: 0, calificada: 0, reincorporada: 0 },
          total_users: 0,
          users_with_guide_stars: 0,
          total_stars_count: 0
        },
        tfiatDistribution: compensationData || {
          total_balance_deductivo: 0,
          total_unrealized_profit: 0,
          total_realized_profit: 0,
          total_company_pending: 0,
          total_company_debt: 0,
          total_company_deductive: 0
        },
        userStats: {
          total_users: starStatus.star_distribution?.total_users || 0,
          active_users: starStatus.star_distribution?.total_users || 0,
          users_with_company_host: starStatus.users_with_company_host || 0,
          company_exists: starStatus.company_exists || false
        },
        systemHealth: {
          is_valid: starStatus.is_valid || false,
          last_distribution: new Date().toISOString(),
          distribution_transactions: 0
        }
      };

      setData(financialData);
    } catch (error: any) {
      console.error('Error al obtener datos del sistema financiero:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al cargar datos del sistema financiero'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchFinancialSystemData();
    setRefreshing(false);
  };

  const handleValidateAndDistribute = async () => {
    try {
      setRefreshing(true);
      await adminService.validateAndDistributeTfiats();
      setMessage({ type: 'success', text: 'Estrellas validadas y distribuidas exitosamente' });
      await fetchFinancialSystemData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al validar y distribuir estrellas'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleDistributeTfiatsControlled = async () => {
    try {
      setRefreshing(true);
      await adminService.distributeTfiatsControlled();
      setMessage({ type: 'success', text: 'Tfiats distribuidos de manera controlada' });
      await fetchFinancialSystemData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al distribuir Tfiats'
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleResetSystem = async () => {
    try {
      setResetting(true);
      const result = await adminService.resetFinancialSystem();
      setMessage({ 
        type: 'success', 
        text: `Sistema reiniciado exitosamente. ${result.details.stars_deleted} estrellas eliminadas, ${result.details.balances_reset} balances reseteados, ${result.details.transactions_deleted} transacciones eliminadas.` 
      });
      setShowResetConfirmation(false);
      await fetchFinancialSystemData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al reiniciar el sistema'
      });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/dashboard')} sx={{ color: 'primary.main' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} color="primary">
            {resetting ? '🔄 Reiniciando Sistema...' : '💰 Sistema Financiero Completo'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing || resetting}
        >
          {refreshing ? <CircularProgress size={20} /> : 'Actualizar'}
        </Button>
      </Box>

      {/* Indicador de Reinicio */}
      {resetting && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="body1" fontWeight={600}>
            🔄 Sistema en proceso de reinicio...
          </Typography>
          <Typography variant="body2">
            Todas las estrellas, balances y transacciones están siendo eliminadas. 
            Esta operación puede tomar unos momentos.
          </Typography>
        </Alert>
      )}

      {/* Mensajes de estado */}
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 4 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Resumen General */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            bgcolor: resetting ? 'grey.400' : 'primary.main', 
            color: 'white',
            opacity: resetting ? 0.7 : 1,
            transition: 'all 0.3s ease'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {resetting ? '...' : (data?.userStats.total_users || 0)}
                  </Typography>
                  <Typography variant="body2">
                    {resetting ? 'Reiniciando...' : 'Usuarios Totales'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            bgcolor: resetting ? 'grey.400' : 'success.main', 
            color: 'white',
            opacity: resetting ? 0.7 : 1,
            transition: 'all 0.3s ease'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <StarIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {resetting ? '0' : (data?.starDistribution.total_stars_count || 0)}
                  </Typography>
                  <Typography variant="body2">
                    {resetting ? 'Reiniciando...' : 'Estrellas Totales'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            bgcolor: resetting ? 'grey.400' : 'warning.main', 
            color: 'white',
            opacity: resetting ? 0.7 : 1,
            transition: 'all 0.3s ease'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MonetizationOnIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {resetting ? '0.00' : (data?.tfiatDistribution.total_balance_deductivo?.toFixed(2) || '0.00')}
                  </Typography>
                  <Typography variant="body2">
                    {resetting ? 'Reiniciando...' : 'Total Tfiats'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            bgcolor: resetting ? 'grey.400' : (data?.systemHealth.is_valid ? 'success.main' : 'error.main'), 
            color: 'white',
            opacity: resetting ? 0.7 : 1,
            transition: 'all 0.3s ease'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon fontSize="large" />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {resetting ? '🔄' : (data?.systemHealth.is_valid ? '✅' : '❌')}
                  </Typography>
                  <Typography variant="body2">
                    {resetting ? 'Reiniciando...' : 'Estado Sistema'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sistema de Estrellas */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            ⭐ Sistema de Estrellas
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom>Distribución por Tipo</Typography>
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Estrellas Guías:</Typography>
                    <Chip label={data?.starDistribution.total_stars.guia || 0} color="primary" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Estrellas Calificadas:</Typography>
                    <Chip label={data?.starDistribution.total_stars.calificada || 0} color="success" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Estrellas Reincorporadas:</Typography>
                    <Chip label={data?.starDistribution.total_stars.reincorporada || 0} color="warning" />
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom>Estadísticas de Usuarios</Typography>
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Usuarios con Estrellas Guías:</Typography>
                    <Chip label={data?.starDistribution.users_with_guide_stars || 0} color="info" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Usuarios con Host Compañía:</Typography>
                    <Chip label={data?.userStats.users_with_company_host || 0} color="secondary" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Estado Válido:</Typography>
                    <Chip 
                      label={data?.systemHealth.is_valid ? 'Sí' : 'No'} 
                      color={data?.systemHealth.is_valid ? 'success' : 'error'} 
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sistema de Tfiats */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🪙 Sistema de Tfiats
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom>Balances Principales</Typography>
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Balance Deductivo:</Typography>
                    <Chip 
                      label={`${data?.tfiatDistribution.total_balance_deductivo?.toFixed(2) || '0.00'} TF`} 
                      color="primary" 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Ganancias No Realizadas:</Typography>
                    <Chip 
                      label={`${data?.tfiatDistribution.total_unrealized_profit?.toFixed(2) || '0.00'} TF`} 
                      color="warning" 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Ganancias Realizadas:</Typography>
                    <Chip 
                      label={`${data?.tfiatDistribution.total_realized_profit?.toFixed(2) || '0.00'} TF`} 
                      color="success" 
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom>Métricas de Compañía</Typography>
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Pendiente:</Typography>
                    <Chip 
                      label={`${data?.tfiatDistribution.total_company_pending?.toFixed(2) || '0.00'} TF`} 
                      color="info" 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Deuda:</Typography>
                    <Chip 
                      label={`${data?.tfiatDistribution.total_company_debt?.toFixed(2) || '0.00'} TF`} 
                      color="error" 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Deductivo:</Typography>
                    <Chip 
                      label={`${data?.tfiatDistribution.total_company_deductive?.toFixed(2) || '0.00'} TF`} 
                      color="secondary" 
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Acciones del Sistema */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🎛️ Acciones del Sistema
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Gestiona el sistema financiero y realiza operaciones administrativas críticas.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<StarIcon />}
              onClick={handleValidateAndDistribute}
              disabled={refreshing}
              size="large"
            >
              {refreshing ? <CircularProgress size={20} /> : 'Validar y Crear Estrellas'}
            </Button>
            
            <Button
              variant="contained"
              color="secondary"
              startIcon={<MonetizationOnIcon />}
              onClick={handleDistributeTfiatsControlled}
              disabled={refreshing}
              size="large"
            >
              {refreshing ? <CircularProgress size={20} /> : 'Distribuir Tfiats Controlado'}
            </Button>

            <Button
              variant="contained"
              color="error"
              startIcon={<WarningIcon />}
              onClick={() => setShowResetConfirmation(true)}
              disabled={resetting || refreshing}
              size="large"
              sx={{
                background: 'linear-gradient(45deg, #ff6b6b, #ee5a52)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #ee5a52, #d63031)',
                }
              }}
            >
              {resetting ? <CircularProgress size={20} /> : '🔄 Reiniciar Sistema'}
            </Button>
          </Box>
          
          {/* Advertencia sobre el reinicio */}
          <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 2 }}>
            <Typography variant="body2" color="warning.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon fontSize="small" />
              <strong>⚠️ Botón de Reinicio:</strong> Esta acción elimina permanentemente todas las estrellas, 
              balances de Tfiats y transacciones del sistema. Solo usar en casos extremos o para pruebas.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Estado de la Compañía */}
      <Card>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🏢 Estado de la Compañía
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom>Información General</Typography>
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Representante de Compañía:</Typography>
                    <Chip 
                      label={data?.userStats.company_exists ? 'Existe' : 'No Existe'} 
                      color={data?.userStats.company_exists ? 'success' : 'error'} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Usuarios con Host Compañía:</Typography>
                    <Chip 
                      label={data?.userStats.users_with_company_host || 0} 
                      color="info" 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Transacciones de Distribución:</Typography>
                    <Chip 
                      label={data?.systemHealth.distribution_transactions || 0} 
                      color="secondary" 
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom>Salud del Sistema</Typography>
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Estado General:</Typography>
                    <Chip 
                      label={data?.systemHealth.is_valid ? 'Saludable' : 'Requiere Atención'} 
                      color={data?.systemHealth.is_valid ? 'success' : 'error'} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Última Distribución:</Typography>
                    <Chip 
                      label={data?.systemHealth.last_distribution ? new Date(data.systemHealth.last_distribution).toLocaleDateString() : 'N/A'} 
                      color="default" 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Progreso del Sistema:</Typography>
                    <Box sx={{ width: '100%' }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={data?.systemHealth.is_valid ? 100 : 50} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Acceso Directo a la Tabla de Distribución */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 Tabla de Distribución de Tfiats
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Accede a la vista completa de distribución de Tfiats y estrellas para todos los usuarios
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/admin/tfiat-distribution')}
              sx={{
                background: 'linear-gradient(45deg, #8B5CF6, #7C3AED)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7C3AED, #6D28D9)',
                }
              }}
            >
              Ver Tabla Completa
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Resumen de Distribución de Tfiats */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <TfiatDistributionSummary />
        </CardContent>
      </Card>

      {/* Modal de Confirmación de Reinicio */}
      {showResetConfirmation && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2
          }}
        >
          <Card sx={{ maxWidth: 500, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              
              <Typography variant="h5" fontWeight={700} color="error" gutterBottom>
                ⚠️ ¡ADVERTENCIA CRÍTICA!
              </Typography>
              
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Reinicio Completo del Sistema Financiero
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Esta acción <strong>NO SE PUEDE DESHACER</strong> y eliminará:
              </Typography>
              
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ❌ <strong>TODAS las estrellas</strong> del sistema
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ❌ <strong>TODOS los balances</strong> de Tfiats (se pondrán en 0)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ❌ <strong>TODAS las transacciones</strong> financieras
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ❌ <strong>TODOS los datos</strong> de compensación
                </Typography>
              </Box>
              
              <Typography variant="body1" color="error" fontWeight={600} sx={{ mb: 3 }}>
                Los usuarios se mantendrán, pero perderán todo su progreso financiero.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setShowResetConfirmation(false)}
                  disabled={resetting}
                  size="large"
                >
                  Cancelar
                </Button>
                
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleResetSystem}
                  disabled={resetting}
                  size="large"
                  startIcon={resetting ? <CircularProgress size={20} /> : <WarningIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #ff6b6b, #ee5a52)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #ee5a52, #d63031)',
                    }
                  }}
                >
                  {resetting ? 'Reiniciando...' : 'SÍ, REINICIAR TODO'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default FinancialSystemPage;
